import { FC, FormEvent } from 'react';
import { FormModal } from '../FormModal';
import { StatusBadge } from '../StatusBadge';
import { AdminService } from '../../../types/admin.types';
import { formatCurrencyAmount } from '../../../utils/currency';

interface ServiceViewModalProps {
  service: AdminService;
  isOpen: boolean;
  onClose: () => void;
}

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
};

export const ServiceViewModal: FC<ServiceViewModalProps> = ({ service, isOpen, onClose }) => {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Service Details"
      submitText="Close"
      cancelText=""
      size="lg"
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Name</label>
          <p className="mt-1 text-sm text-gray-900 dark:text-slate-100">{service.name}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Slug</label>
          <p className="mt-1 text-sm text-gray-900 dark:text-slate-100">{service.slug}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Category</label>
          <p className="mt-1 text-sm text-gray-900 dark:text-slate-100">{service.category}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Required Qualification</label>
          <p className="mt-1 text-sm text-gray-900 dark:text-slate-100">{service.requiredQualification}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Duration</label>
          <p className="mt-1 text-sm text-gray-900 dark:text-slate-100">{formatDuration(service.duration)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Base Price</label>
          <p className="mt-1 text-sm text-gray-900 dark:text-slate-100">{formatCurrencyAmount(service.basePrice)}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Deposit Amount</label>
          <p className="mt-1 text-sm text-gray-900 dark:text-slate-100">
            {service.depositAmount ? formatCurrencyAmount(service.depositAmount) : 'Not required'}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Display Order</label>
          <p className="mt-1 text-sm text-gray-900 dark:text-slate-100">{service.displayOrder}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Status</label>
          <div className="mt-1">
            <StatusBadge status={service.isActive ? 'active' : 'inactive'} />
          </div>
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Description</label>
        <p className="mt-1 text-sm text-gray-900 dark:text-slate-100 whitespace-pre-wrap">{service.description}</p>
      </div>
      {service.imageUrl && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-500 dark:text-slate-400">Image</label>
          <img src={service.imageUrl} alt={service.name} className="mt-2 max-h-48 rounded-md object-cover" />
        </div>
      )}
    </FormModal>
  );
};
